import { getValidClaims } from '@/API/repositories/validClaim';
import Loading from '@/common/components/Loading';
import { useRequestsContext } from '@/common/hooks/requestHook';
import React, { useContext, useEffect, useMemo, useReducer, useState } from 'react';
import { PageWrapper } from '@/common/styles/Wrappers';
import ValidClaimForm from '@/common/components/validClaimForm/ValidClaimForm';
import { sortValidClaims } from './helpers/sortValidClaims';
import ClaimTable from './components/claimTable/ClaimTable';
import ClaimSearchBar from './components/claimSearchBar/ClaimSearchBar';
import AddFile from './components/addFile/AddFile';
import { useMessageQueueContext } from '@/common/hooks/useMessageQueue';
import { useCommonDataContext } from '@/common/hooks/commonDataContext';
import { CLAIM_ROLE_STATUSES } from '@/common/constants/claims';
import { validClaimsReducer } from './helpers/validClaimsReducer';
import {
	DEFAULT_REDUCER_STATE,
	VALID_CLAIMS_ACTIONS,
} from './constants/reducer';
import FilterSetter from './components/filterSetter/FilterSetter';
import { ValidClaimProvider } from '@/common/contexts/validClaimContext';
import { findCalimProvider } from './helpers/findCalimProvider';
import { AuthContext } from '@/common/contexts/authContext';


const ValidClaims = () => {
	const { authUser } = useContext(AuthContext);

	const {
		options: { marketsOptions },
	} = useCommonDataContext();

	const [validClaims, setValidClaims] = useState([]);
	const [selectedMarkets, setSelectedMarkets] = useState(marketsOptions);
	const [selectedStatus, setSelectedStatus] = useState(
		CLAIM_ROLE_STATUSES[authUser.role].TO_DO,
	);
	const [selectedClaim, setSelectedClaim] = useState(null);
	const [addFile, setAddFile] = useState(null);
	const [state, dispatch] = useReducer(
		validClaimsReducer,
		DEFAULT_REDUCER_STATE,
	);
	const [openAction, setOpenAction] = useState(null);

	const { makeRequest, hasUnfilledRequest } = useRequestsContext();
	const { addMessage } = useMessageQueueContext();

	const handleSearch = async () => {
		if (!selectedMarkets?.length || !selectedStatus?.value) {
			return addMessage('Please select markets and statuses', 'error');
		}

		const payload = {
			markets: selectedMarkets.map((market) => market.value),
			statuses: Object.values(selectedStatus.claim_statuses).map(
				(status) => status.value,
			),
		};
		const response = await makeRequest(getValidClaims.bind(null, payload));

		if (!response?.data) {
			return addMessage('Error while loading valid claims', 'error');
		}

		setValidClaims(() => sortValidClaims(response.data));
	};

	const visibleClaims = useMemo(() => {
		return validClaims?.filter((claim) => {
			const marketFilter = state[VALID_CLAIMS_ACTIONS.SET_MARKETS]?.map(
				(m) => m.value,
			);
			const paymentFilter = state[VALID_CLAIMS_ACTIONS.SET_PAYMENT_METHOD]?.map(
				(p) => p.value,
			);

			if (paymentFilter?.length) {
				const claimPaymentProvider = findCalimProvider(claim);
				const claimPaymentProviderLowercase =
					claimPaymentProvider.toLocaleLowerCase();

				if (!paymentFilter.includes(claimPaymentProviderLowercase)) {
					return false;
				}
			}

			if (marketFilter?.length) {
				if (!marketFilter.includes(claim._order.market)) {
					return false;
				}
			}
			return true;
		});
	}, [validClaims, state]);

	const handleSelectStatus = (value) => {
		setValidClaims(() => []);
		setSelectedStatus(() => value);
	};

	useEffect(() => {
		handleSearch();
	}, []);

	return (
		<PageWrapper>
			{hasUnfilledRequest(getValidClaims) && <Loading />}
			<ClaimSearchBar
				selectedMarkets={selectedMarkets}
				setSelectedMarkets={setSelectedMarkets}
				selectedStatus={selectedStatus}
				setSelectedStatus={handleSelectStatus}
				handleSearch={handleSearch}
				filters={state}
				reset={() => dispatch({ type: VALID_CLAIMS_ACTIONS.RESET })}
			/>
			{!!validClaims.length && (
				<ClaimTable
					filters={state}
					setOpenAction={setOpenAction}
					selectedStatus={selectedStatus}
					setAddFile={setAddFile}
					validClaims={visibleClaims}
					setSelectedClaim={setSelectedClaim}
				/>
			)}
			{selectedClaim && (
				<ValidClaimProvider
					savedContact={selectedClaim._contact}
					claim={selectedClaim}
					order={selectedClaim._order}
				>
					<ValidClaimForm reload={handleSearch} setShow={setSelectedClaim} />
				</ValidClaimProvider>
			)}
			{addFile && (
				<AddFile
					claimId={addFile}
					addMessageToParent={addMessage}
					reload={handleSearch}
					setAddFile={setAddFile}
				/>
			)}
			{openAction && (
				<FilterSetter
					openAction={openAction}
					setOpenAction={setOpenAction}
					filter={state}
					dispatchFilter={dispatch}
				/>
			)}
		</PageWrapper>
	);
};

export default ValidClaims;
